import React, { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, updateDoc, collection, getDocs, query, where, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { FaCalendarPlus, FaCheckCircle, FaTimesCircle, FaUserCircle, FaUserMd, FaSpinner, FaTrashAlt } from 'react-icons/fa';
import './agenda.css';

const Agenda = ({ isDarkMode }) => {
  // # State management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [checkType, setCheckType] = useState('study_process');
  const [description, setDescription] = useState('');
  const [mentees, setMentees] = useState([]);
  const [selectedMentee, setSelectedMentee] = useState('');
  const [selectedMenteeName, setSelectedMenteeName] = useState('');
  const [selectedMenteePhoto, setSelectedMenteePhoto] = useState('');
  const [selectedMenteeStep, setSelectedMenteeStep] = useState('');
  const [checkDate, setCheckDate] = useState('');
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingCheck, setSavingCheck] = useState(false);
  const [userRole, setUserRole] = useState(null);

  const auth = getAuth();
  const db = getFirestore();

  // # Get user role on mount
  useEffect(() => {
    const getUserRole = async () => {
      if (auth.currentUser) {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          setUserRole(userDoc.data().role);
        }
      }
    };
    getUserRole();
  }, [auth.currentUser, db]);

  // # Fetch mentor's mentees
  useEffect(() => {
    const fetchMentees = async () => {
      try {
        if (auth.currentUser) {
          setLoading(true);
          
          // # Get user role if not already set
          if (!userRole) {
            const userRef = doc(db, 'users', auth.currentUser.uid);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
              setUserRole(userDoc.data().role);
            }
          }

          // # Get subscription documents where the mentor email matches the current user's email
          const subscriptionsCollection = collection(db, 'subscriptions');
          
          // # Create a list to store all mentee data
          const menteesList = [];
          
          // # Get all subscriptions with the current user's email as mentor
          // # We need to query each subscription type separately
          
          // # Step 1 mentorship subscriptions
          const step1Query = query(
            subscriptionsCollection,
            where('subscriptions.step1_mentorship.mentor.email', '==', auth.currentUser.email),
            where('subscriptions.step1_mentorship.status', '==', 'active')
          );
          
          const step1Snapshot = await getDocs(step1Query);
          for (const subscriptionDoc of step1Snapshot.docs) {
            const data = subscriptionDoc.data();
            const subscription = data.subscriptions?.step1_mentorship;
            if (subscription) {
              // # Get user document
              const userRef = doc(db, 'users', subscriptionDoc.id);
              const userDoc = await getDoc(userRef);
              const userData = userDoc.exists() ? userDoc.data() : {};
              
              menteesList.push({
                id: subscriptionDoc.id,
                menteeId: subscriptionDoc.id,
                email: userData.email || subscription.mentor?.menteeEmail || '',
                subscriptionType: 'step1_mentorship',
                step: 'Step 1',
                name: `${userData.firstName || ''} ${userData.lastName || ''}`,
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                photoURL: userData.photoURL || '/icons/pfp.png',
                username: userData.username || '',
              });
            }
          }
          
          // # Step 2 mentorship subscriptions
          const step2Query = query(
            subscriptionsCollection,
            where('subscriptions.step2_mentorship.mentor.email', '==', auth.currentUser.email),
            where('subscriptions.step2_mentorship.status', '==', 'active')
          );
          
          const step2Snapshot = await getDocs(step2Query);
          for (const subscriptionDoc of step2Snapshot.docs) {
            const data = subscriptionDoc.data();
            const subscription = data.subscriptions?.step2_mentorship;
            if (subscription) {
              // # Get user document
              const userRef = doc(db, 'users', subscriptionDoc.id);
              const userDoc = await getDoc(userRef);
              const userData = userDoc.exists() ? userDoc.data() : {};
              
              menteesList.push({
                id: `${subscriptionDoc.id}_step2`, // # Add suffix to differentiate between steps
                menteeId: subscriptionDoc.id,
                email: userData.email || subscription.mentor?.menteeEmail || '',
                subscriptionType: 'step2_mentorship',
                step: 'Step 2',
                name: `${userData.firstName || ''} ${userData.lastName || ''}`,
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                photoURL: userData.photoURL || '/icons/pfp.png',
                username: userData.username || '',
              });
            }
          }
          
          // # OET subscriptions
          const oetQuery = query(
            subscriptionsCollection,
            where('subscriptions.OET.mentor.email', '==', auth.currentUser.email),
            where('subscriptions.OET.status', '==', 'active')
          );
          
          const oetSnapshot = await getDocs(oetQuery);
          for (const subscriptionDoc of oetSnapshot.docs) {
            const data = subscriptionDoc.data();
            const subscription = data.subscriptions?.OET;
            if (subscription) {
              // # Get user document
              const userRef = doc(db, 'users', subscriptionDoc.id);
              const userDoc = await getDoc(userRef);
              const userData = userDoc.exists() ? userDoc.data() : {};
              
              menteesList.push({
                id: `${subscriptionDoc.id}_oet`,
                menteeId: subscriptionDoc.id,
                email: userData.email || subscription.mentor?.menteeEmail || '',
                subscriptionType: 'OET',
                step: 'OET',
                name: `${userData.firstName || ''} ${userData.lastName || ''}`,
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                photoURL: userData.photoURL || '/icons/pfp.png',
                username: userData.username || '',
              });
            }
          }
          
          // # US Visa counselling subscriptions
          const visaQuery = query(
            subscriptionsCollection,
            where('subscriptions.US_VISA_Counselling.mentor.email', '==', auth.currentUser.email),
            where('subscriptions.US_VISA_Counselling.status', '==', 'active')
          );
          
          const visaSnapshot = await getDocs(visaQuery);
          for (const subscriptionDoc of visaSnapshot.docs) {
            const data = subscriptionDoc.data();
            const subscription = data.subscriptions?.US_VISA_Counselling;
            if (subscription) {
              // # Get user document
              const userRef = doc(db, 'users', subscriptionDoc.id);
              const userDoc = await getDoc(userRef);
              const userData = userDoc.exists() ? userDoc.data() : {};
              
              menteesList.push({
                id: `${subscriptionDoc.id}_visa`,
                menteeId: subscriptionDoc.id,
                email: userData.email || subscription.mentor?.menteeEmail || '',
                subscriptionType: 'US_VISA_Counselling',
                step: 'US Visa',
                name: `${userData.firstName || ''} ${userData.lastName || ''}`,
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                photoURL: userData.photoURL || '/icons/pfp.png',
                username: userData.username || '',
              });
            }
          }
          
          setMentees(menteesList);
          if (menteesList.length > 0) {
            setSelectedMentee(menteesList[0].id);
            setSelectedMenteeName(menteesList[0].name);
            setSelectedMenteePhoto(menteesList[0].photoURL);
            setSelectedMenteeStep(menteesList[0].step);
          }
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching mentees:', error);
        setError('Failed to fetch mentees data');
        setLoading(false);
      }
    };
    
    fetchMentees();
  }, [auth.currentUser, db, userRole]);

  // # Fetch existing checks
  useEffect(() => {
    const fetchChecks = async () => {
      try {
        if (auth.currentUser) {
          setLoading(true);
          
          // # Get the mentor document that contains the mentee_checkups map
          const mentorDocRef = doc(db, 'mentors', auth.currentUser.uid);
          const mentorDoc = await getDoc(mentorDocRef);
          
          if (mentorDoc.exists()) {
            const mentorData = mentorDoc.data();
            const menteeCheckups = mentorData.mentee_checkups || {};
            const checksData = [];
            
            // # Convert the checkups map to an array
            for (const checkupId in menteeCheckups) {
              const checkup = menteeCheckups[checkupId];
              
              // # Get user document for each check
              let menteeData = { photoURL: '/icons/pfp.png', name: checkup.menteeName || 'Unknown' };
              try {
                const userRef = doc(db, 'users', checkup.menteeId);
                const userDoc = await getDoc(userRef);
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  menteeData = {
                    photoURL: userData.photoURL || '/icons/pfp.png',
                    name: `${userData.firstName || ''} ${userData.lastName || ''}`,
                    username: userData.username || '',
                  };
                }
              } catch (err) {
                console.error('Error fetching mentee data:', err);
              }
              
              checksData.push({
                id: checkupId,
                menteeId: checkup.menteeId,
                menteeName: checkup.menteeName,
                menteePhoto: menteeData.photoURL,
                menteeUsername: menteeData.username,
                menteeFull: menteeData.name,
                checkDate: checkup.checkDate,
                checkType: checkup.checkType,
                description: checkup.description,
                completed: checkup.completed || false,
                step: checkup.step || 'Unknown Step',
                createdAt: checkup.createdAt
              });
            }
            
            // # Sort checks by date
            checksData.sort((a, b) => new Date(a.checkDate) - new Date(b.checkDate));
            setChecks(checksData);
          }
          
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching checks:', error);
        setLoading(false);
      }
    };
    
    fetchChecks();
  }, [auth.currentUser, db]);

  // # Open modal with default values
  const handleOpenModal = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    setCheckDate(tomorrow.toISOString().split('T')[0]);
    setCheckType('study_process');
    setDescription('');
    
    if (mentees.length > 0) {
      setSelectedMentee(mentees[0].id);
      setSelectedMenteeName(mentees[0].name);
      setSelectedMenteePhoto(mentees[0].photoURL);
      setSelectedMenteeStep(mentees[0].step);
    }
    
    setIsModalOpen(true);
  };

  // # Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // # Handle mentee selection change
  const handleMenteeChange = (e) => {
    const selectedId = e.target.value;
    setSelectedMentee(selectedId);
    
    // # Find the selected mentee and update related fields
    const mentee = mentees.find(m => m.id === selectedId);
    if (mentee) {
      setSelectedMenteeName(mentee.name);
      setSelectedMenteePhoto(mentee.photoURL);
      setSelectedMenteeStep(mentee.step);
    }
  };

  // # Generate a unique checkup ID
  const generateCheckupId = () => {
    const timestamp = Date.now();
    const randomChars = Math.random().toString(36).substring(2, 12);
    return `chkb-${timestamp}-${randomChars}`;
  };

  // # Save check to Firestore with role handling
  const handleSaveCheck = async () => {
    try {
      if (!selectedMentee || !checkDate || !checkType || !description) {
        alert('Please fill all fields');
        return;
      }

      if (auth.currentUser) {
        setSavingCheck(true);
        const selectedMenteeObj = mentees.find(m => m.id === selectedMentee);
        
        if (!selectedMenteeObj) {
          alert('Selected mentee not found. Please try again.');
          setSavingCheck(false);
          return;
        }
        
        // # Get reference to the mentor document
        const mentorDocRef = doc(db, 'mentors', auth.currentUser.uid);
        const mentorDoc = await getDoc(mentorDocRef);
        
        // # Create a new checkup object
        const checkupId = generateCheckupId();
        const checkupData = {
          menteeId: selectedMenteeObj.menteeId,
          menteeName: selectedMenteeObj.name,
          checkDate,
          checkType,
          description,
          completed: false,
          step: selectedMenteeObj.step,
          createdAt: new Date().toISOString(),
          createdBy: {
            uid: auth.currentUser.uid,
            role: userRole || 'unknown',
            email: auth.currentUser.email
          }
        };
        
        // # Update or create mentor document based on role
        if (mentorDoc.exists()) {
          // # Get current mentee_checkups or create empty object if it doesn't exist
          const currentData = mentorDoc.data();
          const menteeCheckups = currentData.mentee_checkups || {};
          
          // # Add the new checkup to the map
          menteeCheckups[checkupId] = checkupData;
          
          // # Update the document
          await updateDoc(mentorDocRef, {
            mentee_checkups: menteeCheckups
          });
        } else if (userRole === 'tutor') {
          // # Create initial mentor document for tutor
          const menteeCheckups = {
            [checkupId]: checkupData
          };
          
          await setDoc(mentorDocRef, {
            mentee_checkups: menteeCheckups,
            metadata: {
              created_at: new Date().toISOString(),
              last_edited_at: new Date().toISOString()
            }
          });
        }
        
        // # Refresh checks by fetching the updated data
        const updatedMentorDoc = await getDoc(mentorDocRef);
        if (updatedMentorDoc.exists()) {
          const updatedMentorData = updatedMentorDoc.data();
          const updatedMenteeCheckups = updatedMentorData.mentee_checkups || {};
          const checksData = [];
          
          // # Convert the checkups map to an array
          for (const checkupId in updatedMenteeCheckups) {
            const checkup = updatedMenteeCheckups[checkupId];
            
            // # Get user document for each check
            let menteeData = { photoURL: '/icons/pfp.png', name: checkup.menteeName || 'Unknown' };
            try {
              const userRef = doc(db, 'users', checkup.menteeId);
              const userDoc = await getDoc(userRef);
              if (userDoc.exists()) {
                const userData = userDoc.data();
                menteeData = {
                  photoURL: userData.photoURL || '/icons/pfp.png',
                  name: `${userData.firstName || ''} ${userData.lastName || ''}`,
                  username: userData.username || '',
                };
              }
            } catch (err) {
              console.error('Error fetching mentee data:', err);
            }
            
            checksData.push({
              id: checkupId,
              menteeId: checkup.menteeId,
              menteeName: checkup.menteeName,
              menteePhoto: menteeData.photoURL,
              menteeUsername: menteeData.username,
              menteeFull: menteeData.name,
              checkDate: checkup.checkDate,
              checkType: checkup.checkType,
              description: checkup.description,
              completed: checkup.completed || false,
              step: checkup.step || 'Unknown Step',
              createdAt: checkup.createdAt
            });
          }
          
          // # Sort checks by date
          checksData.sort((a, b) => new Date(a.checkDate) - new Date(b.checkDate));
          setChecks(checksData);
        }
        
        setSavingCheck(false);
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error('Error saving check:', error);
      alert('Error saving check. Please try again.');
      setSavingCheck(false);
    }
  };

  // # Toggle check completion
  const toggleCheckCompletion = async (checkId, currentStatus) => {
    try {
      if (auth.currentUser) {
        // # Get reference to the mentor document
        const mentorDocRef = doc(db, 'mentors', auth.currentUser.uid);
        const mentorDoc = await getDoc(mentorDocRef);
        
        if (mentorDoc.exists()) {
          const mentorData = mentorDoc.data();
          const menteeCheckups = mentorData.mentee_checkups || {};
          
          // # Update the completed status for the specific checkup
          if (menteeCheckups[checkId]) {
            menteeCheckups[checkId].completed = !currentStatus;
            
            // # Update the document
            await updateDoc(mentorDocRef, {
              mentee_checkups: menteeCheckups
            });
            
            // # Update local state
            setChecks(checks.map(check => 
              check.id === checkId ? { ...check, completed: !currentStatus } : check
            ));
          }
        }
      }
    } catch (error) {
      console.error('Error toggling check completion:', error);
    }
  };

  // # Format check type for display
  const formatCheckType = (type) => {
    switch(type) {
      case 'study_process': return 'Study Process';
      case 'practice_process': return 'Practice Process';
      case 'task_completion': return 'Task Completion';
      case 'practice_completion': return 'Practice Completion';
      case 'other': return 'Other';
      default: return type;
    }
  };

  // # Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    
    // # Check if the date is valid
    if (isNaN(date.getTime())) return dateString;
    
    // # Format with US month day, year
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // # Get the step color
  const getStepColor = (step) => {
    switch(step) {
      case 'Step 1': return '#4CAF50'; // Green
      case 'Step 2': return '#2196F3'; // Blue
      case 'OET': return '#FF9800'; // Orange
      case 'US Visa': return '#9C27B0'; // Purple
      default: return '#757575'; // Grey
    }
  };

  // # Group checks by date
  const groupChecksByDate = () => {
    const groups = {};
    checks.forEach(check => {
      const date = check.checkDate;
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(check);
    });
    
    // # Convert to array sorted by date
    return Object.entries(groups)
      .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
      .map(([date, checksForDate]) => ({
        date,
        formattedDate: formatDate(date),
        checks: checksForDate
      }));
  };

  // # Determine if date is in the past
  const isDateInPast = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(dateString);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  // # Delete check
  const handleDeleteCheck = async (checkId) => {
    try {
      if (!window.confirm('Are you sure you want to delete this checkup?')) {
        return;
      }
      
      if (auth.currentUser) {
        // # Get reference to the mentor document
        const mentorDocRef = doc(db, 'mentors', auth.currentUser.uid);
        const mentorDoc = await getDoc(mentorDocRef);
        
        if (mentorDoc.exists()) {
          const mentorData = mentorDoc.data();
          const menteeCheckups = mentorData.mentee_checkups || {};
          
          // # Remove the checkup from the map
          if (menteeCheckups[checkId]) {
            delete menteeCheckups[checkId];
            
            // # Update the document
            await updateDoc(mentorDocRef, {
              mentee_checkups: menteeCheckups
            });
            
            // # Update local state
            setChecks(checks.filter(check => check.id !== checkId));
          }
        }
      }
    } catch (error) {
      console.error('Error deleting check:', error);
      alert('Error deleting check. Please try again.');
    }
  };

  return (
    <div className={`agnd_container ${isDarkMode ? 'agnd_dark-mode' : ''}`}>
      <div className="agnd_header">
        <h3>Checkup Agenda</h3>
        <button 
          className="agnd_schedule-btn" 
          onClick={handleOpenModal}
        >
          <FaCalendarPlus className="agnd_schedule-icon" />
          Schedule Check
        </button>
      </div>

      {/* # Checkup checklist */}
      {loading ? (
        <div className="agnd_loading">
          <p>Loading checkups...</p>
        </div>
      ) : (
        <div className="agnd_checklist">
          {checks.length === 0 ? (
            <div className="agnd_no-checks">
              <p>No checks scheduled. Create your first check!</p>
            </div>
          ) : (
            <div className="agnd_timeline">
              {groupChecksByDate().map((group) => (
                <div 
                  key={group.date} 
                  className={`agnd_date-group ${isDateInPast(group.date) ? 'past' : 'upcoming'}`}
                >
                  <div className="agnd_date-header">
                    <div className="agnd_date-bubble">
                      <span className="agnd_date-text">{group.formattedDate}</span>
                    </div>
                  </div>
                  <ul className="agnd_checks-list">
                    {group.checks.map((check) => (
                      <li 
                        key={check.id} 
                        className={`agnd_check-item ${check.completed ? 'completed' : ''}`}
                      >
                        <div className="agnd_check-header">
                          <input
                            type="checkbox"
                            checked={check.completed}
                            onChange={() => toggleCheckCompletion(check.id, check.completed)}
                            className="agnd_check-checkbox"
                          />
                          <div className="agnd_mentee-info">
                            <img 
                              src={check.menteePhoto} 
                              alt={check.menteeName} 
                              className="agnd_mentee-photo"
                            />
                            <div className="agnd_mentee-details">
                              <span className="agnd_mentee-name">{check.menteeName}</span>
                              <span 
                                className="agnd_mentee-step" 
                                style={{ backgroundColor: getStepColor(check.step) }}
                              >
                                {check.step}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="agnd_check-content">
                          <div className="agnd_check-info">
                            <span className="agnd_check-type">{formatCheckType(check.checkType)}</span>
                          </div>
                          <div className="agnd_check-description">
                            <p>{check.description}</p>
                          </div>
                        </div>
                        <div className="agnd_check-status">
                          {check.completed ? (
                            <FaCheckCircle className="agnd_check-status-icon completed" />
                          ) : null}
                          <button 
                            className="agnd_delete-btn" 
                            onClick={() => handleDeleteCheck(check.id)}
                            title="Delete checkup"
                          >
                            <FaTrashAlt />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* # Schedule Check Modal */}
      {isModalOpen && (
        <div className="agnd_modal-overlay">
          <div className="agnd_modal">
            <h3>Schedule a Check</h3>
            
            <div className="agnd_form-group">
              <label>Mentee</label>
              <div className="agnd_mentee-selector">
                {selectedMenteePhoto && (
                  <img 
                    src={selectedMenteePhoto} 
                    alt={selectedMenteeName} 
                    className="agnd_selected-mentee-photo"
                  />
                )}
                <select 
                  value={selectedMentee}
                  onChange={handleMenteeChange}
                  className="agnd_mentee-select"
                >
                  {mentees.map((mentee) => (
                    <option key={mentee.id} value={mentee.id}>
                      {mentee.name} ({mentee.step})
                    </option>
                  ))}
                </select>
              </div>
              {selectedMenteeStep && (
                <div className="agnd_selected-mentee-info">
                  <span 
                    className="agnd_selected-mentee-step"
                    style={{ backgroundColor: getStepColor(selectedMenteeStep) }}
                  >
                    {selectedMenteeStep}
                  </span>
                </div>
              )}
            </div>
            
            <div className="agnd_form-group">
              <label>Check Date</label>
              <input 
                type="date"
                value={checkDate}
                onChange={(e) => setCheckDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="agnd_date-input"
              />
            </div>
            
            <div className="agnd_form-group">
              <label>Check Type</label>
              <select 
                value={checkType}
                onChange={(e) => setCheckType(e.target.value)}
                className="agnd_type-select"
              >
                <option value="study_process">Study Process</option>
                <option value="practice_process">Practice Process</option>
                <option value="task_completion">Task Completion</option>
                <option value="practice_completion">Practice Completion</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div className="agnd_form-group">
              <label>Description</label>
              <textarea 
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter details about what to check..."
                rows={4}
                className="agnd_description-textarea"
              />
            </div>
            
            <div className="agnd_modal-actions">
              <button 
                className="agnd_cancel-btn"
                onClick={handleCloseModal}
                disabled={savingCheck}
              >
                Cancel
              </button>
              <button 
                className="agnd_save-btn"
                onClick={handleSaveCheck}
                disabled={savingCheck}
              >
                {savingCheck ? (
                  <>
                    <FaSpinner className="agnd_spinner" />
                    Saving...
                  </>
                ) : (
                  'Save Check'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agenda;
